import { useMemo } from "react";
import { isPlaidConnection } from "@common/helpers/financialConnection";
import { PlaidConnectionWithAccounts } from "@common/types/plaidConnection";
import { clearConnectionError } from "@helpers/connectionError";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useUserIntegrations } from "@hooks/useUserIntegrations";

interface TaskPlaidConnection {
    plaidConnection: PlaidConnectionWithAccounts | null;
    isLoading: boolean;
}

/**
 * PRO-12946: task actions read the connection from the access-scoped
 * `/user/integrations` data, which applies the same rule the backend enforces
 * (owner, or an entity assignment on an entity the connection feeds).
 * `GET plaid-connection` filters on `userId` alone, so it hid connections a
 * non-owner is allowed to fix.
 */
export function useTaskPlaidConnection(
    connectionId: string | null,
): TaskPlaidConnection {
    const isDemoWorkspace = useActiveWorkspace()?.isDemo ?? false;
    const { data, isLoading } = useUserIntegrations();

    const plaidConnection = useMemo(() => {
        if (!connectionId) {
            return null;
        }

        const match = data?.financialConnections.find(
            ({ connection }) => String(connection.id) === connectionId,
        )?.connection;

        if (!match || !isPlaidConnection(match)) {
            return null;
        }

        return isDemoWorkspace ? clearConnectionError(match) : match;
    }, [connectionId, data, isDemoWorkspace]);

    return { plaidConnection, isLoading };
}
